import { useEffect, useRef } from "react";
import { ExternalLink, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getSelectedGame } from "@/lib/stores/unifiedUserStore";
import { useZoom } from "@/providers/ZoomProvider";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface HighlightContextMenuProps {
  isVisible: boolean;
  position: { x: number; y: number };
  highlightId: string;
  highlightType: string;
  highlightName: string;
  text: string;
  isHighlighted: boolean;
  onClose: () => void;
  onHighlight: () => void;
  onUnhighlight: () => void;
}

export const HighlightContextMenu = ({
  isVisible,
  position,
  highlightId,
  highlightType,
  highlightName,
  text,
  isHighlighted,
  onClose,
  onHighlight,
  onUnhighlight,
}: HighlightContextMenuProps) => {
  const { t } = usePlatformTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { zoom } = useZoom();

  // Close menu when clicking outside
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Delay adding the listener to avoid immediate closure
    setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Adjust position if menu goes outside viewport (compensate for platform zoom)
  useEffect(() => {
    if (isVisible && menuRef.current) {
      const menu = menuRef.current;
      const rect = menu.getBoundingClientRect();

      // Divide by zoom to compensate for container zoom
      let adjustedX = position.x / zoom;
      let adjustedY = position.y / zoom;

      const viewportWidth = window.innerWidth / zoom;
      const viewportHeight = window.innerHeight / zoom;
      const menuWidth = rect.width / zoom;
      const menuHeight = rect.height / zoom;

      if (adjustedX + menuWidth > viewportWidth) {
        adjustedX = viewportWidth - menuWidth - 10;
      }

      if (adjustedY + menuHeight > viewportHeight) {
        adjustedY = viewportHeight - menuHeight - 10;
      }

      menu.style.left = `${adjustedX}px`;
      menu.style.top = `${adjustedY}px`;
    }
  }, [isVisible, position, zoom]);

  const handleOpenInTab = () => {
    const selectedGame = getSelectedGame();
    if (!selectedGame?.id) {
      onClose();
      return;
    }

    // Navigate based on type - tab system will handle opening in new tab automatically
    let path = "";

    switch (highlightType) {
      case "glossary":
        path = `/library/${highlightId}`;
        break;
      case "document":
        path = `/documents/${highlightId}`;
        break;
      case "blueprint":
        path = `/templates/${highlightId}`;
        break;
      default:
        console.log("No navigation defined for type:", highlightType);
        onClose();
        return;
    }

    // Just navigate - the tab system will handle creating the tab
    navigate(path);
    onClose();
  };

  if (!isVisible) return null;

  // Prevent the menu from stealing focus from the editor
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <div
      ref={menuRef}
      className="fixed bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[180px] animate-in fade-in-0 zoom-in-95 duration-200"
      style={{ left: position.x / zoom, top: position.y / zoom, zIndex: 10002 }}
      onMouseDown={handleMouseDown}
    >
      {/* Header with highlight info */}
      <div className="px-3 py-2 border-b border-gray-100">
        <div className="text-xs font-medium text-gray-900 truncate">
          {highlightName || text}
        </div>
        <div className="text-xs text-gray-500 capitalize">{highlightType}</div>
      </div>

      {/* Menu options */}
      <div className="py-1">
        {isHighlighted ? (
          <>
            {/* Unhighlight option */}
            <button
              onClick={() => {
                onUnhighlight();
                onClose();
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <X size={14} className="text-gray-500" />
              <span>{t('platform.editor.highlightcontextmenu.unhighlight')}</span>
            </button>

            {/* Open in tab option - only for navigable types */}
            {["glossary", "document", "blueprint"].includes(highlightType) && (
              <button
                onClick={handleOpenInTab}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <ExternalLink size={14} className="text-gray-500" />
                <span>{t('platform.editor.highlightcontextmenu.open_in_tab')}</span>
              </button>
            )}
          </>
        ) : (
          <>
            {/* Highlight option */}
            <button
              onClick={() => {
                onHighlight();
                onClose();
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Sparkles size={14} className="text-purple-500" />
              <span>{t('platform.editor.highlightcontextmenu.highlight')}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

